import { Location, TitleCasePipe } from '@angular/common';
import { COLOR_PICKER_MORE_TOOL } from '../../../../data/product/color-pickers.data';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, HostBinding, NgZone, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, Subject, Subscription } from 'rxjs';
import * as Prism from 'prismjs';
import { addColorSwatches } from '../../../../util/code-swatches';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-css-extras';
import { BLOG } from 'src/app/data/blog/blog.data';
import { COLOR_ABOUT } from 'src/app/data/tool/tool-about.data';
import { CSS_SYNTAX_DEFAULT, CSS_SYNTAX_PREMIUM, CssSyntaxTheme } from 'src/app/data/theme/css-syntax.data';
import { SERVICES, THEMES, TOOLS } from 'src/app/data/product/product.data';
import { URLS } from '../../../../data/navigation/navigation.data';
import { PaletteGenerationService } from '../../../../service/palette/palette-generation.service';
import { ChartData } from '../../../../model/chart/chart-data.model';
import { Product } from '../../../../model/product/product.model';
import { AnalyticsService } from '../../../../service/analytics/analytics.service';
import { ColorService } from '../../../../service/color/color.service';
import { ProductService } from '../../../../service/product/product.service';
import { RoutingService } from '../../../../service/routing/routing.service';
import { SeoService } from '../../../../service/seo/seo.service';
import { ProductThemeSelection, ThemeService } from '../../../../service/theme/theme.service';

// One copyable/format row in the Color Conversions table.
interface ConversionRow {
  label: string;
  value: string;   // CSS-ready, copyable string
  key: string;     // unique copy key
  link?: string;   // optional /tools/color link (web-safe)
}

// A labelled group of conversion rows (e.g. "Web & sRGB"), rendered with a
// sub-heading row in the conversions table.
interface ConversionGroup {
  title: string;
  rows: ConversionRow[];
}

// One row of the WCAG contrast lab.
interface ContrastRow {
  label: string;
  textColor: string;   // the foreground being measured
  bgColor: string;     // the background it sits on
  ratio: number;
  normal: string;      // 'AAA' | 'AA' | 'Fail'
  large: string;
  key: string;
  apca: number;        // APCA (WCAG 3) Lc contrast
  apcaLevel: string;   // plain-language APCA usage level
}

// One color-blindness deficiency, data-driven (no hard-coded template indices).
interface BlindnessType {
  key: string;
  title: string;
  group: string;       // clinical classification tag
  note: string;        // one-line technical description
  index: number;       // index into colorBlindnessColors.items
}

@Component( {
  selector: 'app-color-page',
  templateUrl: './color-page.component.html',
  styleUrls: [ './color-page.component.scss', '../tools-page/tools-page.component.scss', '../../plugin-pages/plugins-page/plugins-page.component.scss' ],
} )
export class ColorPageComponent implements OnInit, AfterViewInit, OnDestroy {

  URLS = URLS;
  TOOLS = TOOLS;
  SERVICES = SERVICES;

  // "From The Blog": three posts picked at random each load so the section stays
  // fresh and surfaces every post over time (they are all colour related).
  blogCards = [ ...BLOG ].sort( () => Math.random() - 0.5 ).slice( 0, 3 );
  /** the How It Works closer under the blog (the server prints the same four questions as FAQPage) */
  readonly about = COLOR_ABOUT;

  // Left sidebar section nav (blog-style sticky nav + scroll-spy).
  pageSections: { key: string; label: string }[] = [
    { key: 'preview', label: 'Preview' },
    { key: 'channels', label: 'Color Channels' },
    { key: 'conversions', label: 'Conversions' },
    { key: 'schemes', label: 'Harmony' },
    { key: 'shades', label: 'Tints & Shades' },
    { key: 'temperature', label: 'Temperature' },
    { key: 'blindness', label: 'Color Vision' },
    { key: 'palettes', label: 'Palette Examples' },
    { key: 'more', label: 'More Color Tools' },
    { key: 'themes', label: 'Related Themes' },
    { key: 'blog', label: 'From The Blog' },
    { key: 'about', label: 'How It Works' },
    { key: 'reviews', label: 'Reviews' },
  ];
  /** More Color Tools: the Color Picker */
  readonly colorPicker = COLOR_PICKER_MORE_TOOL;
  readonly moreToolsSource = 'color-tool';
  activeSection = 'top';
  private sectionObserver?: IntersectionObserver;

  hex = '';
  // Editable hero hex code: mirrors `hex` but holds whatever the user is typing
  // so the field stays free while typing (only commits to `hex` when valid).
  hexText = '';
  colorName = '';
  formattedColorName = '';
  temperature = '';

  // Becomes true once the first valid color is computed (replaces the old
  // magic-number length guard in the template).
  dataReady = false;

  rgb: ChartData = {
    items: [],
    total: 0,
  };

  hsl = { h: 0, s: 0, l: 0 };

  /** the page's top band starts from the colour itself, not the theme's (draft 2026-10-12; page-band.scss) */
  @HostBinding( 'style.--band' ) get band(): string {
    return `color-mix(in oklab, ${ this.hex || 'var(--theme-color)' } 55%, var(--background-color))`;
  }
  hsv = [];
  lab = [];
  xyz = [];

  selectedColorBlindnessSubType = 'protanomaly';
  selectedColorBlindnessObject = {
    title: '',
    explanation: '',
    colors: [],
  };

  // Drives the color-blindness selector chips + the "All 8" strip. Order matches
  // colorBlindnessColors.items, so `index` maps a chip to its simulated swatch.
  // Ordered so each clinical group is contiguous (Anomalous Trichromacy,
  // Dichromacy, then Incomplete Monochromacy + Monochromacy adjacent). Each
  // entry keeps its `index` into colorBlindnessColors.items regardless of order.
  // Grouped by the affected cone (protan / deutan / tritan, plus monochromacy),
  // each family read from mild (anomaly) to severe (anopia). `index` still maps
  // each type to its simulated swatch regardless of this display order.
  colorBlindnessTypes: BlindnessType[] = [
    { key: 'protanomaly', title: 'Protanomaly', group: 'Protan (red)', note: 'Shifted L-cone (long-wavelength) sensitivity. Reds desaturate and darken.', index: 0 },
    { key: 'protanopia', title: 'Protanopia', group: 'Protan (red)', note: 'No functional L-cones. Red and green hues are confused and reds appear dim.', index: 4 },
    { key: 'deuteranomaly', title: 'Deuteranomaly', group: 'Deutan (green)', note: 'Shifted M-cone (medium-wavelength) sensitivity. The most prevalent deficiency.', index: 1 },
    { key: 'deuteranopia', title: 'Deuteranopia', group: 'Deutan (green)', note: 'No functional M-cones. Red and green hues are confused at normal luminance.', index: 5 },
    { key: 'tritanomaly', title: 'Tritanomaly', group: 'Tritan (blue)', note: 'Shifted S-cone (short-wavelength) sensitivity. Blue and green converge.', index: 2 },
    { key: 'tritanopia', title: 'Tritanopia', group: 'Tritan (blue)', note: 'No functional S-cones. Blue and yellow hues are confused.', index: 6 },
    { key: 'achromatomaly', title: 'Achromatomaly', group: 'Monochromacy (no color)', note: 'Reduced function across all three cone types, yielding a heavily desaturated image.', index: 3 },
    { key: 'achromatopsia', title: 'Achromatopsia', group: 'Monochromacy (no color)', note: 'No functional cones. Perception reduces to luminance, seen as grayscale.', index: 7 },
  ];

  // The same types grouped by clinical category, for one sub-heading per group.
  colorBlindnessGroups = this.colorBlindnessTypes.reduce(
    ( groups: { group: string; types: BlindnessType[] }[], t ) => {
      const last = groups[ groups.length - 1 ];
      if ( last && last.group === t.group ) {
        last.types.push( t );
      } else {
        groups.push( { group: t.group, types: [ t ] } );
      }
      return groups;
    }, [] );

  cmykPercentages: ChartData = {
    items: [],
    total: 0,
  };

  tints = [];
  shades = [];
  tones = [];

  triadicColors = [];
  tetradicColors = [];
  analogousColors = [];
  monochromeColors = [];
  // Three-colour harmony chips (base + two partners), shown beside Triadic /
  // Tetradic. analogousColors / monochromeColors stay at 11 for the hero backdrop.
  analogousTrio = [];
  monochromeTrio = [];

  // Full colour lists per scheme, fed to the harmony wheel beside each chip (base
  // first; the wheel places each colour on the rim by its hue and fans lines from
  // the base). Analogous / Monochrome already carry the base in their middle slot.
  get complementaryScheme(): string[] {
    return [ this.hex, this.complementaryColors[ 1 ] ].filter( Boolean );
  }
  get splitScheme(): string[] {
    return [ this.hex, this.splitComplementaryColors[ 1 ], this.splitComplementaryColors[ 2 ] ].filter( Boolean );
  }
  get triadicScheme(): string[] {
    return [ this.hex, this.triadicColors[ 1 ], this.triadicColors[ 2 ] ].filter( Boolean );
  }
  get tetradicScheme(): string[] {
    return [ this.hex, this.tetradicColors[ 1 ], this.tetradicColors[ 2 ], this.tetradicColors[ 3 ] ].filter( Boolean );
  }
  warmerColors = [];
  coolerColors = [];
  complementaryColors = [];
  splitComplementaryColors = [];

  // --- Redesign view-models (built in updateSelectedColor) ------------------

  // Color Conversions coverage table (grouped for ordering, split into two
  // columns for a compact side-by-side layout).
  conversionGroups: ConversionGroup[] = [];
  conversionColsLeft: ConversionGroup[] = [];
  conversionColsRight: ConversionGroup[] = [];
  webSafe = '';
  webSafeIsSelf = false;
  decimalInt = 0;
  // Channel breakdown bars (RGB 0-255, HSL mixed units, CMYK 0-100%).
  rgbChannels: { label: string; value: number; pct: number; color: string; bg: string }[] = [];
  hslChannels: { label: string; value: string; pct: number; color: string; bg: string }[] = [];
  hsvChannels: { label: string; value: string; pct: number; color: string; bg: string }[] = [];
  cmykChannels: { label: string; value: number; color: string; bg: string }[] = [];
  // Quick-copy hero chips.
  rgbString = '';
  hslString = '';
  hsvString = '';
  // CSS & Variables block.
  cssSnippet = '';
  cssRules: { key: string; html: SafeHtml }[] = [];
  cssByKey: { [ key: string ]: string } = {};   // rule key → its CSS (for sample click-copy)
  // The CSS code block is always painted from a syntax scheme: the active IDE-skin
  // theme's real editor + CSS colours (free themes via their public palette.json,
  // premium themes via bundled CSS_SYNTAX_PREMIUM), or the Codigrate-brand
  // CSS_SYNTAX_DEFAULT when no skin is active.
  editorBg = '';
  editorFg = '';
  synComment = '';
  synSelector = '';
  synProperty = '';
  synValue = '';
  synKeyword = '';
  synPunctuation = '';
  private themeSub?: Subscription;
  private darkLightSub?: Subscription;
  private currentSelection: ProductThemeSelection | null = null;
  private darkLight = 'auto';
  // Preview + WCAG.
  previewTextColor = '#ffffff';      // readable text laid over the color
  textShadowCss = '';                // text-shadow demo (Live Preview + CSS)
  boxShadowCss = '';                 // box-shadow demo (Live Preview + CSS)
  hoveredRule = '';                  // hovered Live Preview sample → highlights its CSS rule
  contrastRows: ContrastRow[] = [];
  pairHex = '#ffffff';               // editable "Against" comparison color
  pairText = 'ffffff';
  pairRatio = 0;
  pairApca = 0;
  pairApcaLevel = '';
  pairNormal = '';
  pairLarge = '';
  // Independent "foreground" test: a picked color shown as text ON this color.
  pairFgHex = '#ffffff';
  pairFgText = 'ffffff';
  pairFgRatio = 0;
  pairFgApca = 0;
  pairFgApcaLevel = '';
  pairFgNormal = '';
  pairFgLarge = '';
  // Ecosystem + cross-links.
  similarColors: string[] = [];
  // Nearest IDE themes, rendered with the shared poster theme-card.
  relatedThemeList: { product: Product; category: string }[] = [];
  // Example palettes generated from this colour (one per scheme), each linking
  // into the full palette tool.
  paletteExamples: { scheme: string; label: string; colors: string[] }[] = [];
  // Clipboard feedback: the key currently showing "Copied".
  copiedKey = '';
  private copiedTimer: any = null;

  // Procedural striped backdrop for the hero, derived from monochrome
  // shades/tones of the selected color. Rebuilt on every color change so the
  // abstract bands react to whatever color the user picks. Soft color stops
  // give the stripes a blurred look.
  heroBackground = '';

  colorBlindnessColors: ChartData = {
    items: [],
    total: 0,
  };

  private colorChangeSubject = new Subject<string>();

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private location: Location,
    private seoService: SeoService,
    private colorService: ColorService,
    private productService: ProductService,
    private routingService: RoutingService,
    private sanitizer: DomSanitizer,
    private themeService: ThemeService,
    private http: HttpClient,
    private zone: NgZone,
    private analyticsService: AnalyticsService,
    private paletteGen: PaletteGenerationService
  ) {
    this.colorChangeSubject.pipe(
      debounceTime( 1000 ) // debounce time of 1 second
    ).subscribe( ( hex ) => {
      this.updatePath();
      // Debounced so a settled colour is one event, not one per keystroke.
      this.analyticsService.logEvent( 'Color Changed', { hex: hex || this.hex } );
    } );
  }

  ngOnInit(): void {
    // iOS Safari's native scroll restoration re-applies the previous scroll on
    // navigation, fighting our scroll-to-top. Hand restoration to the app/router.
    if ( typeof history !== 'undefined' && 'scrollRestoration' in history ) {
      history.scrollRestoration = 'manual';
    }
    // Paint the CSS code block from the active scheme (premium / free theme /
    // default). Re-runs when the theme or the dark/light mode changes.
    this.themeSub = this.themeService.productTheme.subscribe( selection => {
      this.currentSelection = selection;
      this.applyCodeScheme();
    } );
    // The default scheme has separate light/dark JSONs, so re-pick it on a mode
    // change, but only while no theme skin is overriding it.
    this.darkLightSub = this.themeService.darkLightSetting.subscribe( mode => {
      this.darkLight = mode;
      if ( !this.currentSelection ) {
        this.applyCodeScheme();
      }
    } );

    this.route.params.subscribe( ( params ) => {
      if ( params.color ) {
        const foundCode = this.colorService.getHexCode( params.color );
        if ( foundCode ) {
          this.hex = '#' + foundCode.toLowerCase();
          this.hexText = this.hex.substring( 1 ).toLowerCase();
          this.updateSelectedColor();
        } else if ( !this.colorService.isValidHexCode( params.color ) ) {
          this.router.navigate( [ URLS.notFound ] );
          return;
        } else {
          this.hex = '#' + params.color.toLowerCase();
          this.hexText = this.hex.substring( 1 ).toLowerCase();
          this.updateSelectedColor();
        }
        // Force the page to the very top on a fresh navigation. In-page hex edits
        // use location.go() (no route re-fire), so this only runs on real arrivals
        //, fixing iOS Safari, which doesn't reliably reset the body scroll
        // container, leaving the hero tucked under the fixed mobile top bar.
        this.scrollPageToTop();
      } else {
        // Render a random named color IN PLACE (address bar stays on the bare
        // /tools/color), so crawlers never see a JS redirect off the landing.
        this.router.navigate( [ URLS.tools.color + '/' + this.colorService.getRandomNamedColor() ], { skipLocationChange: true } );
      }
    } );
  }

  ngAfterViewInit(): void {
    if ( typeof IntersectionObserver === 'undefined' ) {
      return;
    }
    // Scroll-spy: highlight the sidebar entry for whichever section is centered.
    this.sectionObserver = new IntersectionObserver( entries => {
      let changed = false;
      for ( const entry of entries ) {
        if ( entry.isIntersecting ) {
          const sec = ( entry.target as HTMLElement ).dataset[ 'section' ];
          if ( sec && sec !== this.activeSection ) {
            this.zone.run( () => ( this.activeSection = sec ) );
            changed = true;
          }
        }
      }
      if ( changed ) {
        this.scrollActivePillToStart();
      }
    }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 } );
    // Sections render after the first valid color; observe on the next tick.
    // The hero ([data-section="top"]) is observed too so the colour pill is the
    // selected entry while the page is at the top.
    setTimeout( () => {
      document.querySelectorAll( '[data-section]' ).forEach( el => this.sectionObserver!.observe( el ) );
    } );
  }

  ngOnDestroy(): void {
    this.sectionObserver?.disconnect();
    this.themeSub?.unsubscribe();
    this.darkLightSub?.unsubscribe();
  }

  // Decide which CSS-syntax scheme paints the code block:
  //  - premium theme  -> bundled colours (no public palette.json)
  //  - free theme     -> the theme's public palette.json
  //  - no skin        -> the light/dark default palette JSON
  private applyCodeScheme(): void {
    const sel = this.currentSelection;
    if ( sel?.key && CSS_SYNTAX_PREMIUM[ sel.key ] ) {
      this.applyCssSyntax( CSS_SYNTAX_PREMIUM[ sel.key ] );
    } else if ( sel?.key && sel?.category ) {
      this.loadThemePalette( sel.category, sel.key );
    } else {
      this.loadDefaultPalette();
    }
  }

  // Default code-block colours: a neutral-grey editor surface + Codigrate
  // blue/cyan syntax, with separate JSONs per light/dark mode. The bundled
  // CSS_SYNTAX_DEFAULT is applied first so there is no flash before the fetch.
  private loadDefaultPalette(): void {
    this.applyCssSyntax( CSS_SYNTAX_DEFAULT );
    const mode = this.isDarkMode() ? 'dark' : 'light';
    this.http.get<{ tokens?: any }>( `assets/themes/default/default-${ mode }.palette.json` ).subscribe( {
      next: palette => {
        if ( palette?.tokens ) {
          this.applyThemePalette( palette.tokens );
        }
      },
      error: () => { /* keep the bundled emergency default */ },
    } );
  }

  private isDarkMode(): boolean {
    if ( this.darkLight === 'dark' ) {
      return true;
    }
    if ( this.darkLight === 'light' ) {
      return false;
    }
    return typeof window !== 'undefined' && !!window.matchMedia
      && window.matchMedia( '(prefers-color-scheme: dark)' ).matches;
  }

  // Fetch the active theme's palette.json and map its JetBrains CSS syntax
  // colours onto the code block. Mirrors the theme detail page; on any failure
  // the SCSS accent fallback stays in effect.
  private loadThemePalette( category: string, key: string ): void {
    const url = `assets/themes/${ category }/${ key }-theme/${ key }.palette.json`;
    this.http.get<{ tokens?: any }>( url ).subscribe( {
      next: palette => this.applyThemePalette( palette?.tokens ),
      error: () => this.applyCssSyntax( CSS_SYNTAX_DEFAULT ),
    } );
  }

  // Apply a bundled syntax scheme (default / premium) to the code block.
  private applyCssSyntax( t: CssSyntaxTheme ): void {
    this.editorBg = t.editorBackground;
    this.editorFg = t.editorForeground;
    this.synComment = t.comment;
    this.synSelector = t.selector;
    this.synProperty = t.property;
    this.synValue = t.value;
    this.synKeyword = t.keyword;
    this.synPunctuation = t.punctuation;
  }

  private applyThemePalette( tokens: any ): void {
    if ( !tokens ) {
      this.applyCssSyntax( CSS_SYNTAX_DEFAULT );
      return;
    }
    // Prefer the theme's exact CSS-language syntax colours (tokens.css, baked
    // from the JetBrains scheme) so the block matches the IDE 1:1; fall back to
    // the generic semantic roles for any theme that ships no css block.
    const css = tokens.css ?? {};
    const semantic = tokens.semantic ?? {};
    this.editorBg = tokens.editor?.background ?? tokens.interface?.editorBackground ?? '';
    this.editorFg = tokens.editor?.foreground ?? semantic.plainText ?? '';
    this.synComment = css.comment ?? semantic.comment ?? '';
    this.synSelector = css.selector ?? semantic.selector ?? semantic.tag ?? '';
    this.synProperty = css.property ?? semantic.property ?? semantic.field ?? '';
    this.synValue = css.number ?? css.string ?? semantic.number ?? semantic.string ?? semantic.attributeValue ?? '';
    this.synKeyword = css.keyword ?? semantic.keyword ?? '';
    this.synPunctuation = css.punctuation ?? semantic.punctuation ?? semantic.operator ?? '';
  }

  // Smooth-scroll to a section, clearing the fixed header (and, on mobile, the
  // sticky section bar). Mirrors the blog list: this app scrolls on <body>, so
  // tween body.scrollTop by hand (body.scrollTo smooth is a no-op here).
  scrollToSection( key: string ): void {
    const scroller = ( document.body.scrollHeight > document.body.clientHeight + 4 )
      ? document.body
      : document.documentElement;

    // The colour pill jumps to the very top (the swatch + hex input hero) and
    // becomes the selected entry.
    if ( key === 'top' ) {
      this.activeSection = 'top';
      this.scrollActivePillToStart();
      this.tweenScroll( scroller, 0 );
      return;
    }

    const el = document.getElementById( 'sec-' + key );
    if ( !el ) {
      return;
    }
    this.activeSection = key;
    this.scrollActivePillToStart();

    // clear the nav and the sticky chips' bar under it (draft 2026-10-12)
    let headerOffset = 84;
    const bar = document.querySelector( '.cp-chipbar' ) as HTMLElement | null;
    if ( bar ) {
      const stickyTop = parseFloat( getComputedStyle( bar ).top ) || 72;
      headerOffset = stickyTop + bar.offsetHeight + 16;
    }
    const target = Math.max( 0, el.getBoundingClientRect().top + scroller.scrollTop - headerOffset );
    this.tweenScroll( scroller, target );
  }

  // Instant jump to the very top of the page (all possible scroll roots), run
  // after the view settles so it sticks on iOS Safari.
  private scrollPageToTop(): void {
    const toTop = () => {
      if ( typeof window !== 'undefined' && window.scrollTo ) {
        window.scrollTo( 0, 0 );
      }
      if ( document.scrollingElement ) {
        document.scrollingElement.scrollTop = 0;
      }
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    };
    toTop();
    requestAnimationFrame( toTop );
    // Retry across a few frames: iOS settles its viewport / restores scroll late,
    // so a single reset can be undone before the page is stable.
    [ 0, 80, 200, 400 ].forEach( d => setTimeout( toTop, d ) );
  }

  // Keep the active section's chip in view in the sliding chips' bar (a no-op while every chip fits).
  private scrollActivePillToStart(): void {
    setTimeout( () => {
      const strip = document.querySelector( '.cp-chipbar-row' ) as HTMLElement | null;
      if ( !strip || strip.scrollWidth <= strip.clientWidth ) {
        return;
      }
      const active = strip.querySelector( '.pl-chip.on' ) as HTMLElement | null;
      if ( !active ) {
        return;
      }
      // Centre the active pill by scrolling ONLY the strip horizontally (absolute,
      // so repeated calls don't accumulate). Avoids scrollIntoView, which on iOS
      // also nudges the page's vertical scroll, that read as the page "snapping"
      // to the wrong place while scrolling.
      const sr = strip.getBoundingClientRect();
      const ar = active.getBoundingClientRect();
      const target = strip.scrollLeft + ( ar.left - sr.left ) - ( sr.width - ar.width ) / 2;
      strip.scrollTo( { left: Math.max( 0, target ), behavior: 'smooth' } );
    } );
  }

  private tweenScroll( scroller: HTMLElement, target: number ): void {
    const startTop = scroller.scrollTop;
    const duration = 420;
    const start = performance.now();
    const ease = ( t: number ) => 1 - Math.pow( 1 - t, 3 );
    const step = ( now: number ) => {
      const p = Math.min( 1, ( now - start ) / duration );
      scroller.scrollTop = startTop + ( target - startTop ) * ease( p );
      if ( p < 1 ) {
        requestAnimationFrame( step );
      }
    };
    requestAnimationFrame( step );
  }

  onColorChange( value: string ) {
    this.hexText = this.hex.substring( 1 ).toLowerCase();
    this.updateSelectedColor();
    this.colorChangeSubject.next( value );
  }

  // The hero card is now the shared <app-color-widget>; it emits a validated
  // '#rrggbb' whenever the user picks/types a colour. Commit it like a hero edit
  // (recompute every section + throttle the URL update).
  onWidgetColor( hex: string ) {
    this.hex = hex;
    this.hexText = hex.substring( 1 ).toLowerCase();
    this.updateSelectedColor();
    this.colorChangeSubject.next( hex );
  }

  // Hero hex input: lets the user pick a color by typing a hex code (in addition
  // to clicking the swatch). Keeps the raw text in `hexText` so typing is never
  // fought, and only commits to `hex` (recompute + URL) once it is a valid hex.
  onHexTextChange( value: string ) {
    // Only hex characters are allowed (0-9, a-f); the leading '#' lives in its
    // own box, so the field holds at most six digits.
    const raw = ( value || '' ).replace( /[^0-9a-fA-F]/g, '' ).toLowerCase().slice( 0, 6 );
    this.hexText = raw;
    let v = raw;
    if ( /^[0-9a-fA-F]{3}$/.test( v ) ) {
      v = v.split( '' ).map( c => c + c ).join( '' );
    }
    if ( !this.colorService.isValidHexCode( v ) ) {
      return;
    }
    this.hex = '#' + v.toLowerCase();
    this.updateSelectedColor();
    this.colorChangeSubject.next( this.hex );
  }

  // Enter commits and dismisses the editing underline by blurring the field.
  onHexEnter( event: Event ) {
    ( event.target as HTMLInputElement ).blur();
  }

  // Clicking / focusing the hex field drops the caret at the end (ready to
  // append or backspace) rather than wherever the click landed mid-string.
  // Deferred so it runs after the browser's own caret placement on click.
  onHexFocus( event: Event ) {
    const input = event.target as HTMLInputElement;
    setTimeout( () => {
      const end = input.value.length;
      input.setSelectionRange( end, end );
    } );
  }

  // Block non-hex keystrokes outright (so other letters never even appear).
  // Control keys (backspace, arrows, tab, delete) and shortcuts (Ctrl/Cmd+V,
  // copy, select-all) are single-char-exempt and pass through; pasted text is
  // still sanitized in onHexTextChange.
  onHexKeydown( event: KeyboardEvent ) {
    if ( event.ctrlKey || event.metaKey ) {
      return;
    }
    if ( event.key && event.key.length === 1 && !/[0-9a-fA-F]/.test( event.key ) ) {
      event.preventDefault();
    }
  }

  // "Against" comparison field in the WCAG lab (hex digits only, like the hero).
  onPairTextChange( value: string ) {
    const raw = ( value || '' ).replace( /[^0-9a-fA-F]/g, '' ).toLowerCase().slice( 0, 6 );
    this.pairText = raw;
    // Only commit a full six-digit hex (no three-char shorthand).
    if ( raw.length !== 6 || !this.colorService.isValidHexCode( raw ) ) {
      return;
    }
    this.pairHex = '#' + raw;
    this.buildContrast();
  }

  // Independent "foreground" field: the color tested as text ON this color.
  onPairFgTextChange( value: string ) {
    const raw = ( value || '' ).replace( /[^0-9a-fA-F]/g, '' ).toLowerCase().slice( 0, 6 );
    this.pairFgText = raw;
    // Only commit a full six-digit hex (no three-char shorthand).
    if ( raw.length !== 6 || !this.colorService.isValidHexCode( raw ) ) {
      return;
    }
    this.pairFgHex = '#' + raw;
    this.buildContrast();
  }

  updatePath() {
    // The URL is built from the route, never by dropping the last segment: on the bare landing
    // (/tools/color) that dropped 'color' itself and wrote /tools/000000, a page that does not
    // exist, which is what Mixpanel then recorded as the current url (owner, 2026-09-23).
    const value = this.hex.replace( '#', '' ).toLowerCase();
    const current = this.location.path().split( '?' )[ 0 ];
    const newPath = URLS.tools.color + '/' + value;

    if ( current.toLowerCase() !== newPath ) {
      this.location.go( newPath );

      const paramMap = new Map<string, string>();
      paramMap.set( 'hex', this.hex.replace( '#', '' ) );
      this.seoService.seoAndAnalytics( this.location.path(), paramMap, false );
      // Re-apply the per-colour head over the static route SEO just set.
      this.refreshSeo();
    }
  }

  openPaletteInNewTab( colors ) {
    const route = URLS.tools.palette + '/' + colors.join( '-' ).replace( new RegExp( '#', 'g' ), '' );
    const url = this.router.serializeUrl( this.router.createUrlTree( [ route ] ) );
    window.open( url, '_blank' );
  }

  openRouteInNewTab( route: string ) {
    const url = this.router.serializeUrl( this.router.createUrlTree( [ route ] ) );
    window.open( url, '_blank' );
  }

  // Open a palette example in the full palette tool in a new tab, carrying the
  // scheme + this colour as the source so the subtitle reproduces.
  openPaletteExample( colors: string[], scheme: string ): void {
    const route = URLS.tools.palette + '/' + colors.map( c => c.replace( '#', '' ) ).join( '-' );
    const tree = this.router.createUrlTree( [ route ], { queryParams: { scheme, initializer: this.colorName } } );
    this.analyticsService.logEvent( 'Palette Example Opened', { scheme, from: this.hex } );
    window.open( this.router.serializeUrl( tree ), '_blank' );
  }

  // Single clipboard primitive used by every copy affordance (hero chips,
  // conversion rows, CSS block, preview rules, contrast ratios). Flashes the
  // matching control to "Copied" for ~1.2s via `copiedKey`.
  copy( text: string, key: string ) {
    try {
      navigator.clipboard.writeText( text );
    } catch ( e ) {
      // Clipboard API unavailable; silently ignore.
    }
    // Copying a value is the colour tool's core engagement signal. `format` is
    // the affordance key (hex / rgb / hsl / css / ...) so the report shows which
    // representation visitors take away most.
    this.analyticsService.logEvent( 'Color Copied', { format: key } );
    this.copiedKey = key;
    if ( this.copiedTimer ) {
      clearTimeout( this.copiedTimer );
    }
    this.copiedTimer = setTimeout( () => ( this.copiedKey = '' ), 1200 );
  }

  /** A /tools/color route for a hex value (with or without #). */
  colorRoute( value: string ): string {
    return URLS.tools.color + '/' + ( value || '' ).replace( '#', '' ).toLowerCase();
  }

  updateSelectedColor() {
    this.colorName = this.colorService.getColorName( this.hex.substring( 1 ) );
    this.formattedColorName = this.getFormattedColorName();
    this.refreshSeo();
    this.rgb = this.colorService.hexToRgb( this.hex );
    this.hsl = this.colorService.hexToHsl( this.hex );
    this.hsv = this.colorService.hexToHsv( this.hex );
    this.lab = this.colorService.hexToLab( this.hex );
    this.xyz = this.colorService.hexToXyz( this.hex );
    this.temperature = this.colorService.getColorTemperature( this.hex );
    this.cmykPercentages = this.colorService.hexToCmyk( this.hex );
    this.tints = this.colorService.generateShades( this.hex, 'ffffff', 10 );
    this.shades = this.colorService.generateShades( this.hex, '000000', 10 );
    this.tones = this.colorService.generateShades( this.hex, '808080', 10 );
    this.analogousColors = this.colorService.generateAnalogousColors( this.hex, 11 );
    this.monochromeColors = this.colorService.generateMonochromeColors( this.hex, 11 );
    // Three-colour trios for the harmony chips: generate a five-step ramp (base in
    // the MIDDLE) and keep the outer pair, so the two partners sit a wider step away
    // from the base than a plain 3-step ramp — the colours read as clearly distinct.
    const ana5 = this.colorService.generateAnalogousColors( this.hex, 5 );
    this.analogousTrio = ana5.length >= 5 ? [ ana5[ 0 ], ana5[ 2 ], ana5[ 4 ] ] : ana5;
    const mono5 = this.colorService.generateMonochromeColors( this.hex, 5 );
    this.monochromeTrio = mono5.length >= 5 ? [ mono5[ 0 ], mono5[ 2 ], mono5[ 4 ] ] : mono5;
    this.warmerColors = this.colorService.generateWarmerColors( this.hex, 10 );
    this.coolerColors = this.colorService.generateCoolerColors( this.hex, 10 );
    this.complementaryColors = this.colorService.calculateComplementaryColor( this.hex );
    // Default the WCAG "On #..." comparison to the complementary color; the user
    // can still type any other color afterwards.
    this.pairHex = this.complementaryColors[ 1 ] || this.pairHex;
    this.pairText = this.pairHex.replace( '#', '' );
    this.pairFgHex = this.complementaryColors[ 1 ] || this.pairFgHex;
    this.pairFgText = this.pairFgHex.replace( '#', '' );
    this.splitComplementaryColors = this.colorService.calculateSplitComplementaryColors( this.hex );
    this.triadicColors = this.colorService.calculateTriadicColors( this.hex );
    this.tetradicColors = this.colorService.calculateTetradicColors( this.hex );
    this.colorBlindnessColors.items = this.colorService.generateColorBlindnessColors( this.hex );
    this.setSelectedColorBlindnessColors();
    this.buildConversions();
    this.buildChannels();
    this.buildCss();
    this.buildContrast();
    this.buildSimilarColors();
    this.buildRelatedThemes();
    this.buildPaletteExamples();
    this.buildHeroBackground();
    this.dataReady = true;
  }

  // Example palettes generated from THIS colour with each scheme (plus a warm or
  // cool one to match the colour's temperature). Full-random is excluded since
  // it has no source colour. Each opens in the full palette tool.
  private buildPaletteExamples(): void {
    const schemes: { scheme: string; label: string }[] = [
      { scheme: 'analogous', label: 'Analogous' },
      { scheme: 'monochrome', label: 'Monochrome' },
      { scheme: 'complementary', label: 'Complementary' },
      { scheme: 'split complementary', label: 'Split Complementary' },
      { scheme: 'triadic', label: 'Triadic' },
      { scheme: 'tetradic', label: 'Tetradic' },
      { scheme: this.temperature === 'warm' ? 'warm' : 'cool', label: this.temperature === 'warm' ? 'Warm' : 'Cool' },
    ];
    this.paletteExamples = schemes
      .map( s => ( { scheme: s.scheme, label: s.label, colors: this.paletteGen.paletteFromColor( this.hex, s.scheme ) } ) )
      .filter( e => e.colors.length > 0 );
  }

  // --- View-model builders ---------------------------------------------------

  private buildConversions(): void {
    const r = this.rgb.items[ 0 ]?.value;
    const g = this.rgb.items[ 1 ]?.value;
    const b = this.rgb.items[ 2 ]?.value;
    const [ rp, gp, bp ] = this.colorService.rgbToPercent( this.hex );
    const [ h2, w2, bl2 ] = this.colorService.hexToHwb( this.hex );
    const c = this.cmykPercentages.items;
    const [ okl, oka, okb ] = this.colorService.hexToOklab( this.hex );
    const [ olcl, olcc, olch ] = this.colorService.hexToOklch( this.hex );
    const [ ll, la, lb ] = ( this.lab as any );
    const [ lchl, lchc, lchh ] = this.colorService.hexToLch( this.hex );
    const [ luvL, luvU, luvV ] = this.colorService.hexToLuv( this.hex );
    const xyz = ( this.xyz as any ).map( ( n: number ) => Math.round( n * 1000 ) / 1000 );
    const [ yxyY, yxyX, yxyY2 ] = this.colorService.hexToYxy( this.hex );
    const [ hlL, hlA, hlB ] = this.colorService.hexToHunterLab( this.hex );

    this.rgbString = `rgb(${ r }, ${ g }, ${ b })`;
    this.hslString = `hsl(${ this.hsl.h }, ${ this.hsl.s }%, ${ this.hsl.l }%)`;
    this.hsvString = `hsv(${ this.hsv[ 0 ] }, ${ this.hsv[ 1 ] }%, ${ this.hsv[ 2 ] }%)`;
    this.webSafe = this.colorService.nearestWebSafe( this.hex );
    this.webSafeIsSelf = this.webSafe === this.hex;
    this.decimalInt = this.colorService.hexToDecimalInt( this.hex );
    const ralMatch = this.colorService.nearestRal( this.hex );
    const p3 = this.colorService.hexToDisplayP3( this.hex );

    // Grouped into logical colour-space families, each with a sub-heading row.
    this.conversionGroups = [
      {
        title: 'Web & sRGB',
        rows: [
          { label: 'Hex', value: this.hex.toUpperCase(), key: 'cv-hex' },
          { label: 'RGB', value: this.rgbString, key: 'cv-rgb' },
          { label: 'RGB %', value: `rgb(${ rp }%, ${ gp }%, ${ bp }%)`, key: 'cv-rgbp' },
          { label: 'Web-Safe', value: this.webSafe.toUpperCase(), key: 'cv-websafe', link: this.colorRoute( this.webSafe ) },
          { label: 'Decimal', value: String( this.decimalInt ), key: 'cv-dec' },
          { label: 'Octal', value: this.colorService.hexToOctal( this.hex ), key: 'cv-octal' },
          { label: 'Binary', value: this.colorService.hexToBinary( this.hex ), key: 'cv-bin' },
        ],
      },
      {
        title: 'Hue-based',
        rows: [
          { label: 'HSL', value: this.hslString, key: 'cv-hsl' },
          { label: 'HSV', value: `hsv(${ this.hsv[ 0 ] }, ${ this.hsv[ 1 ] }%, ${ this.hsv[ 2 ] }%)`, key: 'cv-hsv' },
          { label: 'HSB', value: `hsb(${ this.hsv[ 0 ] }, ${ this.hsv[ 1 ] }%, ${ this.hsv[ 2 ] }%)`, key: 'cv-hsb' },
          { label: 'HWB', value: `hwb(${ h2 } ${ w2 }% ${ bl2 }%)`, key: 'cv-hwb' },
        ],
      },
      {
        title: 'Print',
        rows: [
          { label: 'CMYK', value: `cmyk(${ c[ 0 ]?.value }%, ${ c[ 1 ]?.value }%, ${ c[ 2 ]?.value }%, ${ c[ 3 ]?.value }%)`, key: 'cv-cmyk' },
          ...( ralMatch ? [ {
            label: 'RAL',
            value: `RAL ${ ralMatch.code }`,
            key: 'cv-ral',
          } as ConversionRow ] : [] ),
        ],
      },
      {
        title: 'Perceptual & wide gamut',
        rows: [
          { label: 'OKLAB', value: `oklab(${ okl } ${ oka } ${ okb })`, key: 'cv-oklab' },
          { label: 'OKLCH', value: `oklch(${ olcl } ${ olcc } ${ olch })`, key: 'cv-oklch' },
          { label: 'CIELAB', value: `lab(${ ll } ${ la } ${ lb })`, key: 'cv-lab' },
          { label: 'CIELCH', value: `lch(${ lchl } ${ lchc } ${ lchh })`, key: 'cv-lch' },
          { label: 'CIELUV', value: `luv(${ luvL }, ${ luvU }, ${ luvV })`, key: 'cv-luv' },
          { label: 'Display P3', value: `color(display-p3 ${ p3[ 0 ] } ${ p3[ 1 ] } ${ p3[ 2 ] })`, key: 'cv-p3' },
        ],
      },
      {
        title: 'CIE tristimulus',
        rows: [
          { label: 'XYZ', value: `xyz(${ xyz[ 0 ] }, ${ xyz[ 1 ] }, ${ xyz[ 2 ] })`, key: 'cv-xyz' },
          { label: 'Yxy', value: `yxy(${ yxyY }, ${ yxyX }, ${ yxyY2 })`, key: 'cv-yxy' },
          { label: 'Hunter Lab', value: `${ hlL }, ${ hlA }, ${ hlB }`, key: 'cv-hlab' },
        ],
      },
    ];

    // Split the WHOLE groups across two balanced columns (a group is never
    // broken): fill the left column with leading groups until it reaches half of
    // the total rows, put the rest on the right. Both columns are top-aligned, so
    // they start at the same height instead of masonry-flowing unevenly.
    const weight = ( g: ConversionGroup ) => g.rows.length + 1; // +1 for the title row
    const totalWeight = this.conversionGroups.reduce( ( n, g ) => n + weight( g ), 0 );
    let acc = 0;
    let splitAt = this.conversionGroups.length;
    for ( let i = 0; i < this.conversionGroups.length; i++ ) {
      acc += weight( this.conversionGroups[ i ] );
      if ( acc >= totalWeight / 2 ) { splitAt = i + 1; break; }
    }
    this.conversionColsLeft = this.conversionGroups.slice( 0, splitAt );
    this.conversionColsRight = this.conversionGroups.slice( splitAt );
  }

  // RGB channels (filled 0-255) and CMYK channels (filled 0-100%) for the bars.
  private buildChannels(): void {
    const r = Number( this.rgb.items[ 0 ]?.value ) || 0;
    const g = Number( this.rgb.items[ 1 ]?.value ) || 0;
    const b = Number( this.rgb.items[ 2 ]?.value ) || 0;
    this.rgbChannels = [
      this.channel( 'R', r, r / 255 * 100, '#d6493f' ),
      this.channel( 'G', g, g / 255 * 100, '#3aa35e' ),
      this.channel( 'B', b, b / 255 * 100, '#3a74e0' ),
    ];
    // HSL bar colours are derived from the actual color so they read meaningfully:
    // H shows the hue (toned down to 70% sat / 45% light so bright hues like
    // yellow/green/cyan don't wash out), S that hue at its real saturation, L the
    // colour itself.
    const h = this.hsl.h;
    const s = this.hsl.s;
    const l = this.hsl.l;
    this.hslChannels = [
      this.hslChannel( 'H', h + '°', h / 360 * 100, `hsl(${ h }, 70%, 45%)` ),
      this.hslChannel( 'S', s + '%', s, `hsl(${ h }, ${ s }%, 50%)` ),
      this.hslChannel( 'L', l + '%', l, `hsl(${ h }, ${ s }%, ${ l }%)` ),
    ];
    // HSV shares the hue/saturation bars; the V (value/brightness) bar ramps the
    // hue from black to full colour (l = v / 2, so v=100% reads as the pure hue).
    const hv = this.hsv[ 0 ] || 0;
    const sv = this.hsv[ 1 ] || 0;
    const vv = this.hsv[ 2 ] || 0;
    this.hsvChannels = [
      this.hslChannel( 'H', hv + '°', hv / 360 * 100, `hsl(${ hv }, 70%, 45%)` ),
      this.hslChannel( 'S', sv + '%', sv, `hsl(${ hv }, ${ sv }%, 50%)` ),
      this.hslChannel( 'V', vv + '%', vv, `hsl(${ hv }, ${ sv }%, ${ Math.round( vv / 2 ) }%)` ),
    ];
    const c = this.cmykPercentages.items;
    this.cmykChannels = [
      this.cmykChannel( 'C', Number( c[ 0 ]?.value ) || 0, '#16b6d4' ),
      this.cmykChannel( 'M', Number( c[ 1 ]?.value ) || 0, '#d63a9e' ),
      this.cmykChannel( 'Y', Number( c[ 2 ]?.value ) || 0, '#e6c21f' ),
      this.cmykChannel( 'K', Number( c[ 3 ]?.value ) || 0, '#3a3a3a' ),
    ];
  }

  // The unfilled part of every channel track is a faint wash of the channel's
  // own colour (programmatic tint, mirroring the `transient` rgba pattern in
  // ColorService.hexToRgb) instead of a flat grey.
  private readonly CHANNEL_TRACK_ALPHA = 0.16;

  private channel( label: string, value: number, pct: number, color: string ) {
    return { label, value, pct, color, bg: this.colorService.toRgba( color, this.CHANNEL_TRACK_ALPHA ) };
  }

  private hslChannel( label: string, value: string, pct: number, color: string ) {
    return { label, value, pct, color, bg: this.colorService.toRgba( color, this.CHANNEL_TRACK_ALPHA ) };
  }

  private cmykChannel( label: string, value: number, color: string ) {
    return { label, value, color, bg: this.colorService.toRgba( color, this.CHANNEL_TRACK_ALPHA ) };
  }

  private buildCss(): void {
    const r = this.rgb.items[ 0 ]?.value;
    const g = this.rgb.items[ 1 ]?.value;
    const b = this.rgb.items[ 2 ]?.value;
    // Shadow demos shared by the Live Preview and the CSS snippet so the two
    // always stay in sync. Text shadow uses the solid colour; the box shadow
    // uses a softened (semi-transparent) version so it reads like real elevation.
    this.textShadowCss = `2px 2px 4px ${ this.hex }`;
    this.boxShadowCss = `0 8px 24px rgba(${ r }, ${ g }, ${ b }, 0.5)`;
    // Each rule is wrapped (data-rule) so hovering its Live Preview sample can
    // highlight the matching code as if selected (see the [data-hover] CSS).
    const rules = [
      { key: 'text', css: `.text {\n  color: ${ this.hex };\n}` },
      { key: 'background', css: `.background {\n  background-color: ${ this.hex };\n}` },
      { key: 'border', css: `.border {\n  border: 2px solid ${ this.hex };\n}` },
      { key: 'text-shadow', css: `.text-shadow {\n  text-shadow: ${ this.textShadowCss };\n}` },
      { key: 'box-shadow', css: `.box-shadow {\n  box-shadow: ${ this.boxShadowCss };\n}` },
    ];
    this.cssSnippet = rules.map( ( r ) => r.css ).join( '\n\n' );
    // One highlighted block per rule (Angular-rendered span per rule) so a
    // hovered Live Preview sample can light up its matching rule, plus a key→css
    // map so clicking a sample copies that rule.
    this.cssRules = rules.map( ( r ) => ( {
      key: r.key,
      html: this.sanitizer.bypassSecurityTrustHtml(
        addColorSwatches( Prism.highlight( r.css, Prism.languages.css, 'css' ) ) ),
    } ) );
    this.cssByKey = {};
    rules.forEach( ( r ) => ( this.cssByKey[ r.key ] = r.css ) );
  }

  private buildContrast(): void {
    const white = '#ffffff';
    const black = '#000000';
    const onWhite = this.colorService.contrastRatio( this.hex, white );
    const onBlack = this.colorService.contrastRatio( this.hex, black );
    // Text laid OVER the color: whichever of black/white is more legible.
    this.previewTextColor = onWhite >= onBlack ? white : black;
    const mk = ( label: string, textColor: string, bgColor: string, key: string ): ContrastRow => {
      const ratio = this.colorService.contrastRatio( textColor, bgColor );
      const apca = this.colorService.apcaContrast( textColor, bgColor );
      return {
        label, textColor, bgColor, ratio, key,
        normal: this.colorService.wcagLevel( ratio ),
        large: this.colorService.wcagLevel( ratio, true ),
        apca, apcaLevel: this.colorService.apcaLevel( apca ),
      };
    };
    // Rows are interleaved so each opposite pair sits side by side in the 2-col
    // grid: "On white" ↔ "White on", "On black" ↔ "Black on". The colour itself
    // is shown by the swatch, so the labels stay short (no trailing hex / name).
    this.contrastRows = [
      mk( 'On white', this.hex, white, 'wc-white' ),
      mk( 'White on', white, this.hex, 'wc-whiteon' ),
      mk( 'On black', this.hex, black, 'wc-black' ),
      mk( 'Black on', black, this.hex, 'wc-blackon' ),
    ];
    this.pairRatio = this.colorService.contrastRatio( this.hex, this.pairHex );
    this.pairNormal = this.colorService.wcagLevel( this.pairRatio );
    this.pairLarge = this.colorService.wcagLevel( this.pairRatio, true );
    this.pairApca = this.colorService.apcaContrast( this.hex, this.pairHex );
    this.pairApcaLevel = this.colorService.apcaLevel( this.pairApca );
    // Independent foreground test: the picked color as text ON this color.
    this.pairFgRatio = this.colorService.contrastRatio( this.pairFgHex, this.hex );
    this.pairFgNormal = this.colorService.wcagLevel( this.pairFgRatio );
    this.pairFgLarge = this.colorService.wcagLevel( this.pairFgRatio, true );
    this.pairFgApca = this.colorService.apcaContrast( this.pairFgHex, this.hex );
    this.pairFgApcaLevel = this.colorService.apcaLevel( this.pairFgApca );
  }

  // A small, de-duplicated set of nearby colors (hue + lightness nudges) to seed
  // the cross-page internal-link mesh.
  private buildSimilarColors(): void {
    const out: string[] = [];
    const seen = new Set<string>();
    const push = ( hexValue: string ) => {
      const v = ( hexValue || '' ).toLowerCase();
      if ( /^#[0-9a-f]{6}$/.test( v ) && v !== this.hex && !seen.has( v ) ) {
        seen.add( v );
        out.push( v );
      }
    };
    const { h, s, l } = this.hsl;
    [ -30, -20, -10, 10, 20, 30 ].forEach( d => push( '#' + this.colorService.hslToHex( ( h + d + 360 ) % 360, s, l ) ) );
    [ -16, -8, 8, 16 ].forEach( d => push( '#' + this.colorService.hslToHex( h, s, Math.max( 4, Math.min( 96, l + d ) ) ) ) );
    this.similarColors = out.slice( 0, 10 );
  }

  // Nearest IDE themes by CIELAB distance from the color to each theme's
  // palette, so the page links into the theme ecosystem (our edge).
  private buildRelatedThemes(): void {
    // A neutral (achromatic) colour belongs to no colour family, so it must not
    // match colourful themes, but it SHOULD match a genuinely grey theme like
    // Reynisfjara. If the RGB channels are within a few steps (essentially grey,
    // e.g. #d6d6d6), rank themes by how grey their lead colours are and keep only
    // the truly neutral ones.
    const rh = this.hex.replace( '#', '' );
    const cr = parseInt( rh.substring( 0, 2 ), 16 );
    const cg = parseInt( rh.substring( 2, 4 ), 16 );
    const cb = parseInt( rh.substring( 4, 6 ), 16 );
    const spread = ( hexv: string ): number => {
      const h = hexv.replace( '#', '' );
      const pr = parseInt( h.substring( 0, 2 ), 16 );
      const pg = parseInt( h.substring( 2, 4 ), 16 );
      const pb = parseInt( h.substring( 4, 6 ), 16 );
      return Math.max( pr, pg, pb ) - Math.min( pr, pg, pb );
    };
    if ( Math.max( cr, cg, cb ) - Math.min( cr, cg, cb ) <= 6 ) {
      const greyScored: { product: Product; category: string; d: number }[] = [];
      for ( const line of THEMES ) {
        for ( const theme of line.products ) {
          const palette: string[] = ( theme.palette || [] ).filter(
            ( p: string ) => typeof p === 'string' && /^#?[0-9a-fA-F]{6}$/.test( p.replace( '#', '' ) )
          );
          if ( palette.length === 0 ) { continue; }
          const maxSpread = Math.max( ...palette.slice( 0, 6 ).map( spread ) );
          greyScored.push( { product: theme, category: line.name, d: maxSpread } );
        }
      }
      greyScored.sort( ( a, b ) => a.d - b.d );
      // Only genuinely grey themes (low channel-spread) qualify; always show the
      // closest one even if nothing clears the bar.
      const NEUTRAL_MAX = 45;
      let kept = greyScored.filter( ( s ) => s.d <= NEUTRAL_MAX );
      if ( kept.length === 0 ) { kept = greyScored.slice( 0, 1 ); }
      this.relatedThemeList = kept.slice( 0, 3 ).map( ( { d, ...rest } ) => rest );
      return;
    }
    // Dark/light of the colour (LAB lightness) → same-appearance affinity below.
    // The affinity STRENGTH scales with how far the colour is from mid-tone: a
    // clearly dark/light colour strongly prefers same-appearance themes, but a
    // mid-tone colour (e.g. a medium violet) stays appearance-neutral so it isn't
    // wrongly limited to only dark or only light themes.
    const targetL = this.colorService.hexToLab( this.hex )[ 0 ];
    const targetDark = targetL < 50;
    const appStrength = Math.min( 1, Math.abs( targetL - 50 ) / 35 );
    // Match by HUE FAMILY: a colour belongs to a theme mostly by hue, not exact
    // shade, so neon lime still matches a sage-green theme, and a pale blue theme
    // still matches a saturated blue. Hue dominates; saturation/lightness only
    // nudge. Hue is gated by saturation (min of both) so near-grey colours, whose
    // hue is meaningless, fall back to saturation/lightness instead.
    const th = this.colorService.hexToHsl( this.hex.replace( '#', '' ) );
    // A near-neutral target (e.g. a warm taupe-grey) has a hue too weak to gate
    // the normal distance, so it would otherwise match any theme that merely
    // contains a mid-tone grey in its palette, making a warm grey wrongly land
    // on a cool theme (e.g. Salda's turquoise). For these we switch to matching
    // by the theme's CHROMATIC IDENTITY instead (see the grey branch below).
    const targetGrey = th.s < 12;
    const colorDist = ( hex: string ): number => {
      const c = this.colorService.hexToHsl( hex.replace( '#', '' ) );
      let dh = Math.abs( c.h - th.h );
      if ( dh > 180 ) { dh = 360 - dh; }
      const satW = Math.min( c.s, th.s ) / 100;
      const hueTerm = dh * satW;
      const ds = c.s - th.s;
      const dl = c.l - th.l;
      return Math.sqrt( hueTerm * hueTerm + 0.12 * ds * ds + 0.10 * dl * dl );
    };
    const scored: { product: Product; category: string; d: number }[] = [];
    for ( const line of THEMES ) {
      for ( const theme of line.products ) {
        const palette: string[] = ( theme.palette || [] ).filter(
          ( p: string ) => typeof p === 'string' && /^#?[0-9a-fA-F]{6}$/.test( p.replace( '#', '' ) )
        );
        if ( palette.length === 0 ) {
          continue;
        }
        // The theme's real colours are its palette, and the first two entries are
        // its lead/identity colours, they count most. Later accents drop off
        // steeply, so a buried accent match (e.g. Salda's deep teal) can't outrank
        // a theme whose lead colours actually fit (e.g. Sydney/Istanbul for blue).
        // Only the first few palette entries, the theme's lead/identity colours,         // are considered, with the first two weighted strongest. Deep accents are
        // ignored so a near-identical colour buried late in a palette (e.g. a teal
        // at index 9 of the warm Autumn theme) can't hijack a match; identity wins.
        let best = Infinity;
        const LEAD_WEIGHTS = [ 0.8, 0.92, 1.05, 1.15, 1.25, 1.35 ];
        const lead = palette.slice( 0, LEAD_WEIGHTS.length );
        lead.forEach( ( p, i ) => {
          const d = colorDist( p ) * LEAD_WEIGHTS[ i ];
          if ( d < best ) {
            best = d;
          }
        } );
        if ( targetGrey ) {
          // A near-neutral target matches a theme by the grey it contains (above),           // so a neutral/monochrome theme like Reynisfjara, whose palette holds a
          // grey almost identical to the target, is a genuine top match and must
          // stay. But a vivid chromatic theme whose identity hue opposes the grey's
          // faint warm/cool bias (e.g. a cool turquoise/green theme for a warm grey)
          // is a poor match, so we add a penalty for the theme's chromatic identity
          // (its most saturated lead colour) pulling away in hue. Neutral themes
          // have a low-saturation identity, so their penalty is tiny, they stay;
          // opposite-hue colour themes (e.g. Spring) drop out.
          const leadHsl = lead.map( ( p ) => this.colorService.hexToHsl( p.replace( '#', '' ) ) );
          const ident = leadHsl.reduce( ( a, b ) => ( b.s > a.s ? b : a ) );
          let dh = Math.abs( ident.h - th.h );
          if ( dh > 180 ) { dh = 360 - dh; }
          best += dh * ( ident.s / 100 ) * 0.05;
        }
        // Appearance affinity folded into the score: reward a theme whose
        // appearance matches the colour's darkness, penalise the opposite. Sorting
        // by this score then surfaces same-darkness themes first automatically.
        const appearance = ( theme.appearance || '' ).toLowerCase();
        const themeLight = appearance.includes( 'light' ) && !appearance.includes( 'dark' );
        const themeDark = appearance.includes( 'dark' ) && !appearance.includes( 'light' );
        const matches = ( targetDark && themeDark ) || ( !targetDark && themeLight );
        const mismatched = ( targetDark && themeLight ) || ( !targetDark && themeDark );
        const score = best * ( matches ? 1 - 0.3 * appStrength : mismatched ? 1 + 0.5 * appStrength : 1 );
        scored.push( { product: theme, category: line.name, d: score } );
      }
    }
    scored.sort( ( a, b ) => a.d - b.d );
    // Quality gate: show every theme whose (hue-weighted, appearance-adjusted)
    // score reads as a genuine match, up to 6, but always at least the closest,
    // so a colour with no strong match still shows a single suggestion.
    // Relative gate: show themes within a window of the BEST match (adapts to the
    // colour, a colour with one excellent match shows few, a colour with several
    // similar so-so matches shows more), capped by an absolute ceiling so a colour
    // with no real match (pure red/green) still falls through to a single result.
    const best = scored.length ? scored[ 0 ].d : 0;
    const DELTA = 6;
    const ABS = 22;
    const MAX = 3;
    const MIN = 1;
    const good = scored.filter( s => s.d <= best + DELTA && s.d <= ABS ).length;
    const count = Math.min( MAX, Math.max( MIN, good ) );
    this.relatedThemeList = scored.slice( 0, count ).map( ( { d, ...rest } ) => rest );
  }

  /**
   * Compose the hero's backdrop as diagonal bands from the monochrome palette
   * shown in the strip lower on the page, tilted a fixed 30deg to the right.
   * The palette boomerangs (1 2 3 4 5 4 3 2 ...) so the repeat is seamless, and
   * the bands are drawn at reduced opacity over the editor-canvas base. Nothing
   * is randomized - the same color always yields the same backdrop.
   */
  private buildHeroBackground(): void {
    const colors = this.pickHeroColors();
    if ( colors.length === 0 ) {
      this.heroBackground = '';
      return;
    }
    const angle = 115;                                          // fixed 25deg rightward lean, no randomness
    const alpha = this.alphaHex( 0.6 );
    // Boomerang the ramp by appending its reversed interior (1 2 3 4 5 4 3 2),
    // so the band sequence returns to dark smoothly across the span.
    const ramp = colors.concat( colors.slice( 1, -1 ).reverse() );
    // Band width is a share of the backdrop (percent), not a fixed pixel size, so
    // the whole ramp spans left-to-right and the bands widen with the screen.
    const band = 100 / ramp.length;
    // Two stops per color (start + end at the same offset) give each band a hard
    // edge, so the backdrop reads like the swatch strips below.
    const stops: string[] = [];
    ramp.forEach( ( color, i ) => {
      stops.push( `${ color }${ alpha } ${ i * band }%`, `${ color }${ alpha } ${ ( i + 1 ) * band }%` );
    } );
    this.heroBackground = `linear-gradient(${ angle }deg, ${ stops.join( ', ' ) }), var(--background-color)`;
  }

  /**
   * Use the monochrome palette shown in the strip lower on the page, in the
   * same order, as the band sequence. buildHeroBackground boomerangs it for a
   * seamless repeat. The order is identical regardless of the page theme.
   */
  private pickHeroColors(): string[] {
    const out: string[] = [];
    for ( const candidate of this.monochromeColors || [] ) {
      if ( typeof candidate !== 'string' ) {
        continue;
      }
      const hex = candidate.startsWith( '#' ) ? candidate : '#' + candidate;
      if ( /^#[0-9a-fA-F]{6}$/.test( hex ) ) {
        out.push( hex );
      }
    }
    return out;
  }

  /** Two-digit hex alpha (00-ff) for an opacity ratio in [0, 1]. */
  private alphaHex( ratio: number ): string {
    const value = Math.max( 0, Math.min( 255, Math.round( ratio * 255 ) ) );
    return value.toString( 16 ).padStart( 2, '0' );
  }

  setColorBlindnessSubType( colorBlindnessSubType: string ) {
    this.selectedColorBlindnessSubType = colorBlindnessSubType;
    this.setSelectedColorBlindnessColors();
  }

  setSelectedColorBlindnessColors() {
    const colors = [ this.hex ];
    switch ( this.selectedColorBlindnessSubType ) {
      case 'protanomaly' :
      default:
        colors.push( this.colorBlindnessColors.items[ 0 ].value.toString() );
        this.selectedColorBlindnessObject = {
          title: 'Protanomaly',
          explanation: 'Protanomaly is a type of red-weakness where the red photoreceptors in the eyes are less sensitive. ' +
            'People with protanomaly experience reds, oranges, and yellows as appearing greener and less bright. ' +
            'Everyday tasks that involve distinguishing between these colors can be more challenging, especially ' +
            'in low-light conditions. Protanomaly is the most common type of red-green color blindness.',
          colors
        };
        break;
      case 'deuteranomaly' :
        colors.push( this.colorBlindnessColors.items[ 1 ].value.toString() );
        this.selectedColorBlindnessObject = {
          title: 'Deuteranomaly',
          explanation: 'Deuteranomaly is a type of green-weakness where the green photoreceptors are less sensitive. ' +
            'This condition causes greens, oranges, and reds to appear shifted towards red. As a result, ' +
            'it can be difficult to differentiate between these colors. Deuteranomaly is the most common form ' +
            'of color blindness, affecting up to 5% of males but is rare in females.',
          colors
        };
        break;
      case 'tritanomaly' :
        colors.push( this.colorBlindnessColors.items[ 2 ].value.toString() );
        this.selectedColorBlindnessObject = {
          title: 'Tritanomaly',
          explanation: 'Tritanomaly is a type of blue-weakness where blue photoreceptors are less sensitive. ' +
            'This condition makes blues appear greener, and it can be hard to distinguish yellow and red ' +
            'from pink. Tritanomaly is quite rare compared to other types of color blindness and can affect ' +
            'the ability to perform tasks that rely on color differentiation, such as reading colored maps or graphs.',
          colors
        };
        break;
      case 'achromatomaly' :
        colors.push( this.colorBlindnessColors.items[ 3 ].value.toString() );
        this.selectedColorBlindnessObject = {
          title: 'Achromatomaly',
          explanation: 'Achromatomaly is a type of incomplete color blindness where there are fewer functioning cones in the retina. ' +
            'People with achromatomaly have limited color vision and often see a washed-out palette of colors. ' +
            'This condition can make the world appear less vibrant and can affect one\'s ability to perform tasks ' +
            'that require accurate color perception, such as selecting matching clothing or interpreting colored charts.',
          colors
        };
        break;
      case 'protanopia' :
        colors.push( this.colorBlindnessColors.items[ 4 ].value.toString() );
        this.selectedColorBlindnessObject = {
          title: 'Protanopia',
          explanation: 'Protanopia is a type of red-blindness where red photoreceptors are completely absent. ' +
            'People with protanopia have difficulty distinguishing between red and green colors, with reds ' +
            'appearing much darker than they are to those with normal vision. This condition can significantly ' +
            'impact daily activities, such as reading traffic lights or selecting ripe fruits.',
          colors
        };
        break;
      case 'deuteranopia' :
        colors.push( this.colorBlindnessColors.items[ 5 ].value.toString() );
        this.selectedColorBlindnessObject = {
          title: 'Deuteranopia',
          explanation: 'Deuteranopia is a type of green-blindness where green photoreceptors are completely absent. ' +
            'This condition results in a similar effect to protanopia, causing difficulties in distinguishing ' +
            'between red and green colors. However, the brightness of colors is not as affected as in protanopia. ' +
            'Everyday activities, such as gardening or cooking, can be more challenging due to color confusion.',
          colors
        };
        break;
      case 'tritanopia' :
        colors.push( this.colorBlindnessColors.items[ 6 ].value.toString() );
        this.selectedColorBlindnessObject = {
          title: 'Tritanopia',
          explanation: 'Tritanopia is a type of blue-blindness where blue photoreceptors are completely absent. ' +
            'This condition leads to difficulty distinguishing between blue and yellow colors, with blues ' +
            'often appearing as greenish and yellows appearing as light gray. Tritanopia is extremely rare ' +
            'and can impact various aspects of life, including fashion, art, and any tasks involving color coding.',
          colors
        };
        break;
      case 'achromatopsia' :
        colors.push( this.colorBlindnessColors.items[ 7 ].value.toString() );
        this.selectedColorBlindnessObject = {
          title: 'Achromatopsia',
          explanation: 'Achromatopsia is a type of complete color blindness where all cone photoreceptors are absent. ' +
            'Individuals with achromatopsia see the world in shades of gray, as they rely entirely on rod ' +
            'photoreceptors, which detect light intensity but not color. This condition can greatly impact ' +
            'daily life, as it affects the ability to perceive color-coded information and can reduce visual ' +
            'acuity, especially in bright light.',
          colors
        };
        break;
    }
  }

  getFormattedColorName() {
    const titleCasePipe = new TitleCasePipe();
    return titleCasePipe.transform( this.colorName.replace( new RegExp( '-', 'g' ), ' ' ) );
  }

  // Reflect the current colour (its name + hex) in the document head, so each
  // /tools/color/<hex> page carries a unique, indexable title + description
  // instead of the static route one. The static route SEO (seoAndAnalytics) is
  // re-applied on every path change, so this runs right after it as well.
  private refreshSeo(): void {
    // Blank landings render at the bare /tools/color URL (skipLocationChange);
    // its static landing meta must survive, so only real param URLs get the
    // per-color head.
    if ( !this.location.path().split( '?' )[ 0 ].startsWith( URLS.tools.color + '/' ) ) {
      return;
    }
    const hex = ( this.hex || '' ).replace( '#', '' ).toUpperCase();
    if ( !/^[0-9A-F]{6}$/.test( hex ) ) {
      return;
    }
    const name = this.formattedColorName || '';
    // Hex-first, fixed-shape title + description so every /tools/color/<hex>
    // page is a consistent ~58 / ~150 chars (never flagged too short or too
    // long) and matches the server-injected meta in seo-route-meta.js. The
    // colour name still rides along in the keywords for named-colour queries.
    const title = `#${ hex } Color Codes - Hex, RGB, HSL, HSV & CMYK | Codigrate`;
    const description = `#${ hex } color values by Codigrate: RGB, HSL, HSV, CMYK, LAB and OKLCH conversions, tints, shades, complementary and analogous schemes with WCAG contrast.`;
    this.seoService.setMetaData( {
      title,
      description,
      keywords: [
        `#${ hex.toLowerCase() }`, `${ hex.toLowerCase() } hex`,
        ...( name ? [ name.toLowerCase(), `${ name.toLowerCase() } color` ] : [] ),
        'hex color', 'rgb', 'hsl', 'cmyk', 'ral', 'hex to ral', 'ral color', 'color converter', 'contrast checker', 'codigrate',
      ],
      robots: [ 'index', 'follow', 'max-image-preview:large', 'max-snippet:-1' ],
    } );
  }

  redirect( url: string ) {
    this.routingService.redirect( url );
  }
}
